import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { INITIAL_STUDENT_ENROLLMENT_VALUES } from "../components/student/studentEnrollmentConstants";
import { fetchHostelsForEnrollment, findHostelIdForRoom } from "../utils/hostelEnrollmentOptions";
import { studentMatchesHostel } from "../utils/hostelGenderUtils";
import { getSchoolIdForUser } from "../utils/userContext";
import {
    defaultResidenceType,
    getSchoolBoardingFromUser,
    normalizeBoardingMode,
    requiresHostelAssignment,
    schoolOffersBoarding,
    showResidencePicker,
} from "../utils/boardingUtils";
import { validateNewPassword } from "../utils/passwordValidation";
import { fetchNextStudentId } from "../utils/studentIdUtils";

export function useStudentEnrollmentForm({ currentUser, selectedClass, initialClassId = "" }) {
    const [values, setValues] = useState(() => ({
        ...INITIAL_STUDENT_ENROLLMENT_VALUES,
        sclassName: initialClassId || "",
    }));
    const [photoFile, setPhotoFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [hostels, setHostels] = useState([]);
    const [boardingOverride, setBoardingOverride] = useState(null);
    const fileInputRef = useRef(null);

    const schoolId = getSchoolIdForUser(currentUser);

    const { boardingMode, hasBoarding } = useMemo(() => {
        if (boardingOverride) return boardingOverride;
        return getSchoolBoardingFromUser(currentUser);
    }, [boardingOverride, currentUser]);

    const offersBoarding = schoolOffersBoarding(boardingMode, hasBoarding);
    const showResidenceSection = offersBoarding;
    const canPickResidence = showResidencePicker(boardingMode, hasBoarding);

    const selectedEducationLevel = selectedClass?.educationLevel || "";
    const isUniversity = selectedEducationLevel === "University";
    const showGuardianSection = Boolean(selectedEducationLevel) && !isUniversity;

    const setField = useCallback((name, value) => {
        setValues((prev) => ({ ...prev, [name]: value }));
    }, []);

    const resetForm = useCallback(
        (keepClassId = true) => {
            const classId = keepClassId ? values.sclassName : initialClassId || "";
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
            setPhotoFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            setValues({
                ...INITIAL_STUDENT_ENROLLMENT_VALUES,
                sclassName: classId,
                residenceType: defaultResidenceType(boardingMode, hasBoarding),
            });
        },
        [boardingMode, hasBoarding, initialClassId, previewUrl, values.sclassName],
    );

    useEffect(() => {
        if (initialClassId && !values.sclassName) {
            setField("sclassName", initialClassId);
        }
    }, [initialClassId, setField, values.sclassName]);

    useEffect(() => {
        const classId = values.sclassName;
        if (!classId) {
            setField("rollNum", "");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchNextStudentId(classId);
                if (!cancelled && data?.nextStudentId != null) {
                    setField("rollNum", String(data.nextStudentId));
                }
            } catch {
                if (!cancelled) setField("rollNum", "");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [values.sclassName, setField]);

    useEffect(() => {
        setField("residenceType", defaultResidenceType(boardingMode, hasBoarding));
    }, [boardingMode, hasBoarding, setField]);

    /** Teachers may lack top-level boardingMode; load from school (Admin) record. */
    useEffect(() => {
        if (!schoolId) return;
        const fromUser = getSchoolBoardingFromUser(currentUser);
        if (schoolOffersBoarding(fromUser.boardingMode, fromUser.hasBoarding)) {
            setBoardingOverride(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/Admin/${schoolId}`);
                if (cancelled || !data) return;
                const mode = normalizeBoardingMode(data.boardingMode, data.hasBoarding);
                if (schoolOffersBoarding(mode, data.hasBoarding)) {
                    setBoardingOverride({ boardingMode: mode, hasBoarding: data.hasBoarding });
                }
            } catch {
                /* keep user-derived defaults */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [schoolId, currentUser]);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    useEffect(() => {
        if (!offersBoarding || !schoolId) {
            setHostels([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const rows = await fetchHostelsForEnrollment(schoolId);
                if (!cancelled) setHostels(rows);
            } catch {
                if (!cancelled) setHostels([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [offersBoarding, schoolId]);

    const handlePhotoPick = useCallback(
        (event) => {
            const f = event.target.files?.[0];
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
            }
            if (!f) {
                setPhotoFile(null);
                return;
            }
            setField("photoUrl", "");
            setPhotoFile(f);
            setPreviewUrl(URL.createObjectURL(f));
        },
        [previewUrl, setField],
    );

    const clearPhotoPick = useCallback(() => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPhotoFile(null);
        setField("photoUrl", "");
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [previewUrl, setField]);

    const validateForSubmit = useCallback(() => {
        if (!values.sclassName) {
            return "Please select a class.";
        }
        if (!values.name?.trim()) {
            return "Full name is required.";
        }
        const rollFromForm = Number(values.rollNum);
        if (!Number.isFinite(rollFromForm) || rollFromForm < 1) {
            return "Student ID is not ready yet — select a class and wait a moment.";
        }
        const passwordErr = validateNewPassword(values.password);
        if (passwordErr) return passwordErr;
        if (isUniversity && !values.studentRegNumber?.trim()) {
            return "Student registration number is required for university students.";
        }
        if (requiresHostelAssignment(boardingMode, hasBoarding, values.residenceType) && !values.hostelRoomId) {
            return "Boarding students must be assigned a hostel room.";
        }
        if (values.residenceType === "Boarding" && values.hostelRoomId) {
            const hid = values.hostelId || findHostelIdForRoom(hostels, values.hostelRoomId);
            const hostel = hostels.find((h) => h.id === hid);
            const match = studentMatchesHostel(values.gender, hostel?.gender);
            if (!match.ok) return match.message;
        }
        return null;
    }, [boardingMode, hasBoarding, isUniversity, values, hostels]);

    const formContext = useMemo(
        () => ({
            values,
            setField,
            selectedClass,
            selectedEducationLevel,
            isUniversity,
            showGuardianSection,
            showResidenceSection,
            canPickResidence,
            boardingMode,
            hostels,
            photoFile,
            previewUrl,
            fileInputRef,
            handlePhotoPick,
            clearPhotoPick,
        }),
        [
            values,
            setField,
            selectedClass,
            selectedEducationLevel,
            isUniversity,
            showGuardianSection,
            showResidenceSection,
            canPickResidence,
            boardingMode,
            hostels,
            photoFile,
            previewUrl,
            handlePhotoPick,
            clearPhotoPick,
        ],
    );

    return {
        values,
        setValues,
        setField,
        photoFile,
        resetForm,
        validateForSubmit,
        formContext,
        isUniversity,
        showGuardianSection,
        showResidenceSection,
        boardingMode,
    };
}
